import { Checkbox } from "../../Checkbox/index.js";
import { Tooltip } from "../../Tooltip/index.js";
import React from "react";
import { IconArrowDown, IconLoader } from "@synthesis/shared-icons";
import { jsx, jsxs } from "react/jsx-runtime";
import { useVirtualizer } from "@tanstack/react-virtual";
//#region src/TableListColumnFilter/local-components/GroupedCheckboxList.tsx
const ITEM_HEIGHT = 40;
const GROUP_HEADER_HEIGHT = 24;
const GroupedCheckboxList = ({ groups, selectedItems, onChange, hasNextPage, isFetching, getItemId, getItemLabel, getItemTooltip, onScrollEnd, groupsCollapsed = true }) => {
	const [openedGroups, setOpenedGroups] = React.useState({});
	const parentRef = React.useRef(null);
	const scrollEndRef = React.useRef(onScrollEnd);
	const isLoaderVisible = hasNextPage || isFetching;
	scrollEndRef.current = onScrollEnd;
	const toggleOpen = (label) => {
		setOpenedGroups((prev) => {
			const current = prev[label] ?? !groupsCollapsed;
			return {
				...prev,
				[label]: !current
			};
		});
	};
	const virtualizer = useVirtualizer({
		count: hasNextPage || isFetching ? groups.length + 1 : groups.length,
		getScrollElement: () => parentRef.current,
		estimateSize: (index) => {
			if (index === groups.length && isLoaderVisible) return ITEM_HEIGHT;
			const group = groups[index];
			if (!group) return GROUP_HEADER_HEIGHT;
			if (!group.label) return group.items.length * ITEM_HEIGHT;
			if (openedGroups[group.label] ?? !groupsCollapsed) return group.items.length * ITEM_HEIGHT + GROUP_HEADER_HEIGHT;
			return GROUP_HEADER_HEIGHT;
		},
		getItemKey: (index) => groups[index]?.label ?? "loader_element",
		overscan: 2
	});
	const virtualItems = virtualizer.getVirtualItems();
	const lastVirtualItemIndex = virtualItems.at(-1)?.index;
	React.useEffect(() => {
		virtualizer.measure();
	}, [openedGroups, virtualizer]);
	React.useEffect(() => {
		if (hasNextPage && !isFetching && lastVirtualItemIndex === groups.length) scrollEndRef.current?.();
	}, [
		groups.length,
		hasNextPage,
		isFetching,
		lastVirtualItemIndex
	]);
	return /* @__PURE__ */ jsx("div", {
		ref: parentRef,
		style: { maxHeight: `${Math.min(groups.length || 5, 8) * 2.5}rem` },
		className: "[&::-webkit-scrollbar-thumb]:bg-neutrals-80 h-full min-h-0 w-full overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar-thumb]:w-2 [&::-webkit-scrollbar-thumb]:rounded-[10px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:mr-2 [&::-webkit-scrollbar]:w-2",
		children: /* @__PURE__ */ jsx("ul", {
			className: "relative w-full",
			style: { height: `${virtualizer.getTotalSize()}px` },
			children: virtualItems.map((virtualItem) => {
				if (virtualItem.index === groups.length) return /* @__PURE__ */ jsx("li", {
					"data-index": virtualItem.index,
					className: "absolute left-0 top-0 w-full",
					style: { transform: `translateY(${virtualItem.start}px)` },
					children: /* @__PURE__ */ jsx("div", {
						className: "flex h-10 cursor-progress items-center justify-center",
						children: /* @__PURE__ */ jsx(IconLoader, { className: "size-6 animate-spin" })
					})
				}, virtualItem.key);
				const group = groups[virtualItem.index];
				if (!group) return null;
				const isSelectedBlock = !group.label;
				const isOpen = openedGroups[group.label] ?? !groupsCollapsed;
				return /* @__PURE__ */ jsx("li", {
					"data-index": virtualItem.index,
					className: "absolute left-0 top-0 w-full",
					style: { transform: `translateY(${virtualItem.start}px)` },
					children: /* @__PURE__ */ jsxs("div", { children: [!isSelectedBlock ? /* @__PURE__ */ jsx("button", {
						type: "button",
						className: "text-type-light w-full cursor-pointer truncate border-none bg-transparent pl-2.5 pt-1 text-left text-sm outline-none",
						onClick: () => toggleOpen(group.label),
						title: group.label,
						children: /* @__PURE__ */ jsxs("span", {
							className: "flex flex-nowrap items-center",
							children: [/* @__PURE__ */ jsx("span", {
								className: "truncate",
								children: group.label
							}), /* @__PURE__ */ jsx(IconArrowDown, {
								className: "size-5 shrink-0 transition-all data-[open=true]:rotate-180",
								"data-open": isOpen
							})]
						})
					}) : null, /* @__PURE__ */ jsx("ul", {
						className: "data-[open=false]:hidden",
						"data-open": isSelectedBlock || isOpen,
						children: group.items.map((item) => {
							const id = getItemId?.(item) ?? item.id ?? "";
							const label = getItemLabel?.(item) ?? item.label;
							const tooltip = getItemTooltip?.(item) ?? item.tooltip ?? label;
							const content = /* @__PURE__ */ jsx("div", {
								className: "h-10 overflow-y-hidden px-2.5 py-2",
								children: /* @__PURE__ */ jsx(Checkbox, {
									className: "w-full min-w-0",
									label: /* @__PURE__ */ jsx("span", {
										className: "block truncate",
										children: label ?? item.label
									}),
									checked: selectedItems[id] ?? false,
									onChange: () => onChange(id)
								})
							});
							return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Tooltip, {
								description: tooltip,
								triggerEvent: "mouse",
								arrowSide: "left",
								children: content
							}) }, id);
						})
					})] })
				}, virtualItem.key);
			})
		})
	});
};
//#endregion
export { GroupedCheckboxList };

//# sourceMappingURL=GroupedCheckboxList.js.map